import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Col, Container, Form, Row } from "react-bootstrap";
import { object } from "yup";
import { generatePath, useLocation, useNavigate, useParams } from "react-router-dom";
import KhanReportLogo from "components/shared/ReportComponents/KhanReortLogo";
import { useGetFeedbackStepDetailQuery, useSubmitFeedbackStepMutation } from "store/services/clients/assessmentTreatmentPlan";
import { formatReportFeedback, greetingFieldPlaceholders, reportInitialValues } from "helpers/assessment";
import { reportQuestionsValidationSchema } from "utils/validation/validationHelpers";
import RenderQuestion, { RenderQuestionById } from "components/shared/ReportComponents/Questions/Index";

// img
import logo from "../../../../../Assets/images/logo.png";
import { Formik } from "formik";
import { CalendarIcon, CheckIcon, EditCricleIcon } from "Assets/icons";
import { dateFormat, getAge, userFullName } from "helpers/common";
import LoadingIndicator from "components/shared/LoadingIndicator";
import ErrorComponent from "components/shared/ErrorComponent";
import LoadingOverlay from "components/shared/LoadingOverlay";
import { CLIENT_REPORT_TYPES, DISCHARGE_SUMMARY_REPORT_QUESTIONS, REPORT_IDS } from "config/constants";
import LoadingButton from "components/shared/LoadingButton";
import { DISCHARGE_SUMMARY_REPORT_PREVIEW } from "config/paths";
import TreatmentSummaryGoalsReport from "components/shared/ReportComponents/TreatmentSummary/TreatmentSummaryGoalsReport";
import GuardianDetailRow from "components/shared/ReportComponents/GuardianDetailRow";
import ReportHeader from "components/shared/ReportComponents/ReportHeader/Index";
import moment from "moment";
import { CSVLink } from "react-csv";
import ReportError from "../components/ReportError";

const DischargeSummaryReport = () => {
  const navigate = useNavigate();
  const { assessmentId, clientId, clientType } = useParams();
  const [csvData, setCsvData] = useState([]);
  const csvLinkRef = useRef();

  const [reportHeading, setReportHeading] = useState(CLIENT_REPORT_TYPES[REPORT_IDS.DISCHARGE_SUMMARY_REPORT].title);
  const [editingReportHeading, setEditingReportHeading] = useState(false);

  const [submitStep, { isLoading: submittingStep }] = useSubmitFeedbackStepMutation();
  const { data, isLoading, isFetching, isError, refetch } = useGetFeedbackStepDetailQuery({ assessmentId, clientId, clientType });
  const assessmentGoals = data?.assesmentData?.reportGoals;
  const assessmentAssignedId = data?.assesment?.id;
  const reportQuestions = data?.assesmentData?.reportQuestions;
  const caseloadId = data?.clientPendingCaseload?.id;

  const caseDiscipline = data?.clientPendingCaseload?.clientCaseloadTherapist?.caseDiscipline;
  const caseTherapist = data?.clientPendingCaseload?.clientCaseloadTherapist?.clientCaseloadMapping?.find(x => x.caseTherapist?.therapist_detail?.type == "veteran")?.caseTherapist;
  const therapistDetails = { caseDiscipline, caseTherapist, discipline_id: data?.clientPendingCaseload?.clientCaseloadTherapist?.discipline_id }

  const initialValues = useMemo(() => {
    if (!data || !reportQuestions) return {};
    let answers = data?.assesmentData?.reportAnswers;
    let values = {
      documents: data?.assesmentData?.reportDocuments,
      resources: data?.assesmentData?.reportResources,
    };

    values = reportInitialValues(values, reportQuestions, answers);

    return values;
  }, [data, reportQuestions]);

  // console.log("initialValues", initialValues)

  const validationSchema = useMemo(() => {
    if (!reportQuestions) return object();

    return reportQuestionsValidationSchema(reportQuestions);
  }, [reportQuestions])

  const handleChangeReportHeading = (e) => {
    setReportHeading(e.target.value);
  };

  const handleSubmitStep = async (values) => {
    try {
      let payload = formatReportFeedback(reportHeading, reportQuestions, values);

      let response = await submitStep({
        assessmentAssignedId,
        data: payload,
        clientType
      });

      if (!response?.error) {
        navigate(generatePath(DISCHARGE_SUMMARY_REPORT_PREVIEW, {
          assessmentId,
          clientId,
          clientType
        }));
      }

    } catch (err) {
      console.error("Error submitting step three", err);
    }
  };

  const handleDownloadCsv = () => {
    try {
      csvLinkRef.current.link.click();

    } catch (err) { console.log("Error creating csv") };
  };

  useEffect(() => {

    const dataArray = [[
      "Title",
      "Context",
      "Discipline",
      // "Description",
      "Notes"]];

    data?.previousGoals?.map(item => dataArray.push([
      item?.assesmentGoalDetail?.title || "-",
      item?.assesmentGoalContext?.context || "-",
      item?.assesmentGoalDiscipline?.assign_term || "-",
      // item?.goal_description || "-",
      item?.goal_notes || "-",
    ]));

    setCsvData(dataArray);
  }, [data]);

  const renderQuestion = useCallback(
    (props) => (
      <RenderQuestionById
        reportQuestions={reportQuestions}
        {...props}
      />
    ),
    [reportQuestions]
  );

  return (
    <>
      <section className="ReportSec py-3 position-relative">
        <Container fluid>
          <Formik
            initialValues={initialValues}
            onSubmit={handleSubmitStep}
            validationSchema={validationSchema}
            enableReinitialize
          >
            {({ handleSubmit, values, errors }) => (
              <Form onSubmit={handleSubmit}>
                {isLoading ? (
                  <LoadingIndicator />
                ) : <Row>
                  <Col lg="12" className="my-2">
                    <div className="cardCstm p-lg-5 rounded-2 bg-white shadow">
                      <div className="top text-center position-relative">
                        <CSVLink
                          data={csvData}
                          ref={csvLinkRef}
                          style={{ display: "none" }}
                          filename={`${data?.firstname}_${data?.lastname}_${reportHeading}_review.csv`?.replace(/ /g, "_".toLocaleLowerCase())}
                        />
                        <Button
                          className="d-flex fw-bold position-absolute align-items-center justify-content-center commonBtn bg-transparent themeClr"
                          style={{ right: 0, top: 0 }}
                          onClick={handleDownloadCsv}
                        >
                          Download Reviews
                        </Button>
                      </div>
                      {isError ? (
                        <ErrorComponent />
                      ) : (
                        <>
                          {isFetching && <LoadingOverlay />}
                          <ReportHeader
                            initialHeading={data?.assesment?.report_heading || reportHeading}
                            therapistDetails={therapistDetails}
                            setHeading={setReportHeading}
                          />
                          <div className="formBody pt-3">
                            <div
                              className=""
                            // style={{ borderBottom: "2px dashed #6c6c6c" }}
                            >
                              <div className="py-3">
                                <div
                                  className="cardCstm rounded-2 px-3"
                                  style={{ background: "rgb(249, 249, 249)" }}
                                >
                                  <div className="border-bottom p-2 row">
                                    <div className="py-2 col-md-6">
                                      <div className="d-flex align-items-start gap-10 flex-wrap">
                                        <h6
                                          className="m-0 fw-sbold themeBlue"
                                          style={{ minWidth: 200, fontSize: 14 }}
                                        >
                                          Last Name
                                        </h6>
                                        <p className="m-0 themeBlue">
                                          {data?.lastname}
                                        </p>
                                      </div>
                                    </div>
                                    <div className="py-2 col-md-6">
                                      <div className="d-flex align-items-start gap-10 flex-wrap">
                                        <h6
                                          className="m-0 fw-sbold themeBlue"
                                          style={{ minWidth: 200, fontSize: 14 }}
                                        >
                                          First Name
                                        </h6>
                                        <p className="m-0 themeBlue">
                                          {data?.firstname}
                                        </p>
                                      </div>
                                    </div>
                                  </div>
                                  <div className="border-bottom p-2 row">
                                    <div className="py-2 col-md-6">
                                      <div className="d-flex align-items-start gap-10 flex-wrap">
                                        <h6
                                          className="m-0 fw-sbold themeBlue"
                                          style={{ minWidth: 200, fontSize: 14 }}
                                        >
                                          Date of Birth:
                                        </h6>
                                        <p className="m-0 themeBlue">
                                          <span className="icn me-2">
                                            <CalendarIcon />
                                          </span>
                                          {dateFormat(data?.client_detail?.dob)}
                                        </p>
                                      </div>
                                    </div>
                                    <div className="py-2 col-md-6">
                                      <div className="d-flex align-items-start gap-10 flex-wrap">
                                        <h6
                                          className="m-0 fw-sbold themeBlue"
                                          style={{ minWidth: 200, fontSize: 14 }}
                                        >
                                          Age:
                                        </h6>
                                        <p className="m-0 themeBlue">
                                          {getAge(data?.client_detail?.dob)}
                                        </p>
                                      </div>
                                    </div>
                                  </div>
                                  <div className="border-bottom p-2 row">
                                    <div className="py-2 col-md-6">
                                      <div className="d-flex align-items-start gap-10 flex-wrap">
                                        <h6
                                          className="m-0 fw-sbold themeBlue"
                                          style={{ minWidth: 200, fontSize: 14 }}
                                        >
                                          Report Date:
                                        </h6>
                                        <p className="m-0 themeBlue">
                                          <span className="icn me-2">
                                            <CalendarIcon />
                                          </span>
                                          {dateFormat(moment())}
                                        </p>
                                      </div>
                                    </div>
                                    <div className="py-2 col-md-6">
                                      <div className="d-flex align-items-start gap-10 flex-wrap">
                                        <h6
                                          className="m-0 fw-sbold themeBlue"
                                          style={{ minWidth: 200, fontSize: 14 }}
                                        >
                                          Clinician Name:
                                        </h6>
                                        <p className="m-0 themeBlue text-capitalize">{userFullName(therapistDetails?.caseTherapist)}</p>
                                      </div>
                                    </div>
                                  </div>
                                  <div className="border-bottom p-2 row">
                                    {clientType == "school" && <div className="py-2 col-md-6">
                                      <div className="d-flex align-items-start gap-10 flex-wrap">
                                        <h6
                                          className="m-0 fw-sbold themeBlue"
                                          style={{ minWidth: 200, fontSize: 14 }}
                                        >
                                          School Name:
                                        </h6>
                                        <p className="m-0 themeBlue">
                                          {data?.client_detail?.client_school_detail?.client_school?.name}
                                        </p>
                                      </div>
                                    </div>}
                                    <div className="py-2 col-md-6">
                                      <div className="d-flex align-items-start gap-10 flex-wrap">
                                        <h6
                                          className="m-0 fw-sbold themeBlue"
                                          style={{ minWidth: 200, fontSize: 14 }}
                                        >
                                          City/Town:
                                        </h6>
                                        <p className="m-0 themeBlue">
                                          {data?.client_detail?.city_town}
                                        </p>
                                      </div>
                                    </div>
                                  </div>
                                  {/* Guardian Details */}
                                  {data?.client_detail?.client_guardian_mapping?.length > 0 &&
                                    data?.client_detail?.client_guardian_mapping?.map(
                                      (guardian, index) => {
                                        const slug = guardian?.client_guardian?.client_role?.slug;
                                        if (slug !== (clientType == "school" ? "teacher" : "parent")) {
                                          return null;
                                        }

                                        return (
                                          <GuardianDetailRow
                                            index={index}
                                            guardian={guardian}
                                            slug={slug}
                                          />
                                        );
                                      }
                                  )}
                                </div>
                              </div>
                              {reportQuestions?.length > 0 &&
                                reportQuestions?.map((question, index) => {
                                  if (
                                    [
                                      DISCHARGE_SUMMARY_REPORT_QUESTIONS.greeting,
                                      DISCHARGE_SUMMARY_REPORT_QUESTIONS.recommendation,
                                      DISCHARGE_SUMMARY_REPORT_QUESTIONS.goal_notes,
                                      DISCHARGE_SUMMARY_REPORT_QUESTIONS.treatment_summary,
                                    ].includes(question.id)
                                  ) {
                                    return null;
                                  }

                                  return (
                                    <div className="py-3">
                                      <RenderQuestion
                                        key={index}
                                        question={question}
                                        fieldName={question.id}
                                      />
                                    </div>
                                  );
                                }
                                )}
                              <div className="py-3">
                                <RenderQuestionById
                                  questionId={DISCHARGE_SUMMARY_REPORT_QUESTIONS.treatment_summary}
                                  reportQuestions={reportQuestions}
                                />
                                <TreatmentSummaryGoalsReport
                                  clientType={clientType}
                                  fieldValue={
                                    values[DISCHARGE_SUMMARY_REPORT_QUESTIONS.treatment_summary]?.other_details
                                  }
                                  caseloadId={caseloadId}
                                  renderQuestion={renderQuestion}
                                  questionId={DISCHARGE_SUMMARY_REPORT_QUESTIONS.treatment_summary}
                                  assessmentId={assessmentId}
                                />
                              </div>
                              <div className="py-3">
                                <RenderQuestionById
                                  questionId={DISCHARGE_SUMMARY_REPORT_QUESTIONS.recommendation}
                                  reportQuestions={reportQuestions}
                                />
                              </div>
                              <div className="py-3">
                                <RenderQuestionById
                                  questionId={DISCHARGE_SUMMARY_REPORT_QUESTIONS.greeting}
                                  reportQuestions={reportQuestions}
                                  showQuestion={false}
                                  fieldData={greetingFieldPlaceholders(therapistDetails)}
                                />
                              </div>
                            </div>
                            <div className="">
                              {/* <div className="py-3">
                                <h6 className="m-0 fw-sbold themeBlue pb-1">
                                  Add Signature
                                </h6>
                                <div className="d-flex align-items-center gap-10">
                                  <input
                                    type="text"
                                    className="form-control bg-white"
                                    style={{ maxWidth: 300 }}
                                  />
                                  <Button className="border-0 p-0" variant="transparent">
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      width="28"
                                      height="28"
                                      viewBox="0 0 28 28"
                                      fill="none"
                                    >
                                      <path
                                        d="M12.667 15.3346V19.3346C12.667 19.7124 12.7948 20.0291 13.0503 20.2846C13.3059 20.5402 13.6225 20.668 14.0003 20.668C14.3781 20.668 14.6948 20.5402 14.9503 20.2846C15.2059 20.0291 15.3337 19.7124 15.3337 19.3346V15.3346H19.3337C19.7114 15.3346 20.0281 15.2069 20.2837 14.9513C20.5392 14.6957 20.667 14.3791 20.667 14.0013C20.667 13.6235 20.5392 13.3069 20.2837 13.0513C20.0281 12.7957 19.7114 12.668 19.3337 12.668H15.3337V8.66797C15.3337 8.29019 15.2059 7.97352 14.9503 7.71797C14.6948 7.46241 14.3781 7.33463 14.0003 7.33463C13.6225 7.33463 13.3059 7.46241 13.0503 7.71797C12.7948 7.97352 12.667 8.29019 12.667 8.66797V12.668H8.66699C8.28921 12.668 7.97255 12.7957 7.71699 13.0513C7.46144 13.3069 7.33366 13.6235 7.33366 14.0013C7.33366 14.3791 7.46144 14.6957 7.71699 14.9513C7.97255 15.2069 8.28921 15.3346 8.66699 15.3346H12.667ZM14.0003 27.3346C12.1559 27.3346 10.4225 26.9846 8.80032 26.2846C7.1781 25.5846 5.76699 24.6346 4.56699 23.4346C3.36699 22.2346 2.41699 20.8235 1.71699 19.2013C1.01699 17.5791 0.666992 15.8457 0.666992 14.0013C0.666992 12.1569 1.01699 10.4235 1.71699 8.8013C2.41699 7.17908 3.36699 5.76797 4.56699 4.56797C5.76699 3.36797 7.1781 2.41797 8.80032 1.71797C10.4225 1.01797 12.1559 0.667969 14.0003 0.667969C15.8448 0.667969 17.5781 1.01797 19.2003 1.71797C20.8225 2.41797 22.2337 3.36797 23.4337 4.56797C24.6337 5.76797 25.5837 7.17908 26.2837 8.8013C26.9837 10.4235 27.3337 12.1569 27.3337 14.0013C27.3337 15.8457 26.9837 17.5791 26.2837 19.2013C25.5837 20.8235 24.6337 22.2346 23.4337 23.4346C22.2337 24.6346 20.8225 25.5846 19.2003 26.2846C17.5781 26.9846 15.8448 27.3346 14.0003 27.3346Z"
                                        fill="#B46C9F"
                                      ></path>
                                    </svg>
                                  </Button>
                                </div>
                              </div> */}
                              <div className="py-3">
                                <img
                                  src={
                                    therapistDetails?.caseTherapist
                                      ?.therapist_detail?.signature_img
                                  }
                                  style={{ height: 70 }}
                                  alt=""
                                  className="img-fluid object-fit-contain"
                                />
                                <p className="m-0 fw-sbold" style={{ whiteSpace: "pre-wrap" }}>
                                  {therapistDetails?.caseTherapist?.therapist_detail?.email_signature}
                                </p>
                              </div>
                              <div className="py-3">
                                <KhanReportLogo />
                              </div>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </Col>
                  <Col lg="12" className="my-2">
                    <ReportError />
                    <LoadingButton
                      type="submit"
                      className="d-flex align-items-center justify-content-center commonBtn"
                      isLoading={submittingStep}
                    >
                      Preview
                    </LoadingButton>
                  </Col>
                </Row>}
              </Form>
            )}
          </Formik>
        </Container>
      </section>
    </>
  );
};

export default DischargeSummaryReport;
